/**
 * generateRaciHtml — Generates a print-ready RACI Matrix HTML document.
 *
 * Opens a new window with a formatted RACI table (tasks × roles),
 * then triggers the browser's native print dialog.
 *
 * Follows the same pattern as generateBomHtml.ts for consistency.
 *
 * Epic: DEV-625
 */

import type { TaskRoleAssignmentEnriched } from '../../api/ganttTaskRoleAssignments';
import i18n from '../../i18n/config';

/* Cast to bypass i18next v25 strict 0-arg type signature */
const t = i18n.t.bind(i18n) as (key: string, opts?: Record<string, string | number>) => string;

/* ── HTML escaping ───────────────────────────────────────────── */

function esc(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ── RACI letter/class ───────────────────────────────────────── */

const raciMap: Record<string, string> = {
  responsible: 'R',
  accountable: 'A',
  consulted: 'C',
  informed: 'I',
};

const raciColorMap: Record<string, string> = {
  responsible: '#16a34a',
  accountable: '#2563eb',
  consulted: '#d97706',
  informed: '#6b7280',
};

/* ── Data types matching TeamRaciView's processed data ───────── */

export interface PrintableTask {
  id: string;
  taskName: string;
  isSummary: boolean;
  indentLevel: number;
  percentComplete: number;
}

export interface PrintableRole {
  roleSlotGuid: string;
  roleLabel: string;
}

/* ── Main generator ──────────────────────────────────────────── */

export function openRaciPrintWindow(
  tasks: PrintableTask[],
  roles: PrintableRole[],
  assignments: TaskRoleAssignmentEnriched[],
  projectName: string,
  currentUserRoleGuids?: Set<string>,
): void {
  const now = new Date();
  const dateStr = now.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  /* Build assignment lookup: taskId(upper) → roleSlotGuid(upper) → type */
  const assignmentMap = new Map<string, Map<string, string>>();
  for (const a of assignments) {
    const taskKey = a.ganttTaskId.toUpperCase();
    const roleKey = a.roleSlotGuid.toUpperCase();
    if (!assignmentMap.has(taskKey)) assignmentMap.set(taskKey, new Map());
    assignmentMap.get(taskKey)!.set(roleKey, a.responsibilityType);
  }

  /* ── Header row ────────────────────────────────────────────── */
  let headerCells = `<th class="task-col">${esc(t('projectTeam:raci.taskColumn'))}</th>`;
  for (const role of roles) {
    const isMine = currentUserRoleGuids?.has(role.roleSlotGuid.toUpperCase()) ?? false;
    headerCells += `<th class="role-col${isMine ? ' my-role' : ''}">${esc(role.roleLabel)}${isMine ? '<span class="you-badge">' + esc(t('projectTeam:raci.you') || 'You') + '</span>' : ''}</th>`;
  }

  /* ── Body rows ─────────────────────────────────────────────── */
  let bodyRows = '';
  for (const task of tasks) {
    const rowClass = task.isSummary ? 'phase-row' : 'data-row';
    const indent = task.indentLevel * 16;
    const completeDot = !task.isSummary && task.percentComplete >= 100
      ? '<span class="status-dot"></span>'
      : '';

    let cells = `<td class="task-cell" style="padding-inline-start: ${indent + 8}px;">${completeDot}${task.isSummary ? '<strong>' : ''}${esc(task.taskName)}${task.isSummary ? '</strong>' : ''}</td>`;

    for (const role of roles) {
      const rType = assignmentMap.get(task.id.toUpperCase())?.get(role.roleSlotGuid.toUpperCase());
      const isMine = currentUserRoleGuids?.has(role.roleSlotGuid.toUpperCase()) ?? false;
      const cellClass = isMine ? ' my-role-cell' : '';

      if (rType && raciMap[rType]) {
        const color = raciColorMap[rType] || '#1a1a1a';
        cells += `<td class="raci-cell${cellClass}"><span style="color:${color};font-weight:700;">${raciMap[rType]}</span></td>`;
      } else {
        cells += `<td class="raci-cell${cellClass}"></td>`;
      }
    }

    bodyRows += `<tr class="${rowClass}">${cells}</tr>\n`;
  }

  /* ── Summary counts ────────────────────────────────────────── */
  const totalTasks = tasks.filter((t) => !t.isSummary).length;
  const completeTasks = tasks.filter((t) => !t.isSummary && t.percentComplete >= 100).length;
  const totalAssignments = assignments.length;

  /* ── Build full HTML ───────────────────────────────────────── */
  const html = `<!DOCTYPE html>
<html lang="${i18n.language}" dir="${i18n.dir()}">
<head>
  <meta charset="utf-8">
  <title>${esc(t('projectTeam:raci.printTitle'))} — ${esc(projectName)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 9pt;
      line-height: 1.4;
      color: #1a1a1a;
      padding: 20px 24px;
    }

    /* ── Header ── */
    .raci-header {
      border-bottom: 3px solid #1a1a1a;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .raci-header h1 {
      font-size: 16pt;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .raci-header .project-name {
      font-size: 12pt;
      font-weight: 500;
      color: #444;
    }
    .raci-meta {
      display: flex;
      justify-content: space-between;
      margin-top: 6px;
      font-size: 8.5pt;
      color: #666;
    }

    /* ── Summary strip ── */
    .summary-strip {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      background: #f5f5f5;
      border-radius: 5px;
      padding: 10px 14px;
      margin-bottom: 18px;
      font-size: 9pt;
    }
    .summary-strip .stat { font-weight: 600; }
    .summary-strip .stat-label { color: #666; margin-inline-end: 3px; }

    /* ── Legend ── */
    .legend {
      display: flex;
      gap: 16px;
      margin-bottom: 14px;
      font-size: 8.5pt;
      color: #555;
    }
    .legend-item { display: flex; align-items: center; gap: 4px; }
    .legend-letter { font-weight: 700; font-size: 10pt; }

    /* ── Table ── */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.5pt;
      table-layout: auto;
    }
    thead th {
      background: #f0f0f0;
      text-align: center;
      font-weight: 600;
      padding: 6px 4px;
      border-bottom: 2px solid #bbb;
      white-space: normal;
      font-size: 8pt;
      vertical-align: bottom;
    }
    .task-col {
      text-align: start;
      min-width: 180px;
      padding-inline-start: 8px;
    }
    .role-col {
      width: 60px;
      min-width: 50px;
      max-width: 80px;
    }
    .my-role {
      background: #dbeafe !important;
    }
    .my-role-cell {
      background: #eff6ff;
    }
    .you-badge {
      display: block;
      font-size: 7pt;
      font-weight: 700;
      color: #2563eb;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 2px;
    }

    tbody td {
      padding: 4px 4px;
      border-bottom: 1px solid #e5e5e5;
      vertical-align: middle;
    }
    .task-cell {
      text-align: start;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 300px;
    }
    .raci-cell {
      text-align: center;
      font-size: 10pt;
    }

    /* Phase rows */
    .phase-row td {
      background: #f5f5f5;
      border-bottom: 1px solid #ccc;
    }

    /* Zebra striping */
    .data-row:nth-child(even) td {
      background: #fafafa;
    }
    .data-row:nth-child(even) .my-role-cell {
      background: #dbeafe;
    }

    /* Status dot (complete tasks) */
    .status-dot {
      display: inline-block;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #2563eb;
      margin-inline-end: 5px;
      vertical-align: middle;
    }

    /* ── Print overrides ── */
    @media print {
      body { padding: 0; font-size: 8pt; }
      .no-print { display: none !important; }
      .summary-strip { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      thead th { background: #f0f0f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .my-role { background: #dbeafe !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .my-role-cell { background: #eff6ff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .phase-row td { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .status-dot { background: #2563eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
    }

    /* ── Print button ── */
    .print-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #1a1a1a;
      color: #fff;
      padding: 8px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      z-index: 9999;
    }
    .print-bar button {
      background: #3b82f6;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 6px 18px;
      font-size: 10pt;
      cursor: pointer;
      font-weight: 600;
    }
    .print-bar button:hover { background: #2563eb; }
    .print-bar-spacer { height: 48px; }

    /* ── Footer ── */
    .raci-footer {
      margin-top: 24px;
      border-top: 1px solid #ccc;
      padding-top: 6px;
      font-size: 7.5pt;
      color: #999;
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="print-bar no-print">
    <span>${esc(t('projectTeam:raci.printTitle'))} — ${esc(projectName)}</span>
    <button onclick="window.print()">${esc(t('projectTeam:raci.printBtn') || 'Print')}</button>
  </div>
  <div class="print-bar-spacer no-print"></div>

  <div class="raci-header">
    <h1>${esc(t('projectTeam:raci.printTitle'))}</h1>
    <div class="project-name">${esc(projectName)}</div>
    <div class="raci-meta">
      <span>${esc(dateStr)}</span>
      <span>${totalTasks} ${esc(t('projectTeam:raci.printTasks') || 'tasks')} · ${roles.length} ${esc(t('projectTeam:raci.printRoles') || 'roles')} · ${totalAssignments} ${esc(t('projectTeam:raci.printAssignments') || 'assignments')}</span>
    </div>
  </div>

  <div class="legend">
    <div class="legend-item"><span class="legend-letter" style="color:#16a34a;">R</span> = ${esc(t('projectTeam:raci.responsible'))}</div>
    <div class="legend-item"><span class="legend-letter" style="color:#2563eb;">A</span> = ${esc(t('projectTeam:raci.accountable'))}</div>
    <div class="legend-item"><span class="legend-letter" style="color:#d97706;">C</span> = ${esc(t('projectTeam:raci.consulted'))}</div>
    <div class="legend-item"><span class="legend-letter" style="color:#6b7280;">I</span> = ${esc(t('projectTeam:raci.informed'))}</div>
  </div>

  <div class="summary-strip">
    <div>
      <span class="stat-label">${esc(t('projectTeam:raci.printTasks') || 'Tasks')}:</span>
      <span class="stat">${totalTasks}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('projectTeam:raci.printComplete') || 'Complete')}:</span>
      <span class="stat">${completeTasks} / ${totalTasks}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('projectTeam:raci.printRoles') || 'Roles')}:</span>
      <span class="stat">${roles.length}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('projectTeam:raci.printAssignments') || 'Assignments')}:</span>
      <span class="stat">${totalAssignments}</span>
    </div>
  </div>

  <table>
    <thead>
      <tr>${headerCells}</tr>
    </thead>
    <tbody>
      ${bodyRows}
    </tbody>
  </table>

  <div class="raci-footer">
    ${esc(t('projectTeam:raci.printFooter') || 'Generated from Life4Earth')} — ${esc(dateStr)}
  </div>
</body>
</html>`;

  /* ── Open window & trigger print ────────────────────────────── */
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    // Popup blocker — fall back to blob download
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RACI-${projectName.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
}
